// Developer: Ankit Gupta
/**
 * Typed client for DataPilot Chat (backend/app/api/v1/chat.py).
 *
 * The Slack bridges' conversations inside the product: channels and the
 * caller's role in each, conversations scoped to a project, their messages
 * by kind, the Allow / Deny cards, next-step buttons, the help menu, and the
 * files that go in and out.
 */
import apiClient, { api } from '@/api/client';

const BASE = '/chat';

export type Role = 'owner' | 'consultant' | 'viewer';
export type MessageKind = 'user' | 'assistant' | 'system' | 'status' | 'card' | 'buttons' | 'menu' | 'file' | 'action' | 'patch';

export interface Channel {
  key: string;
  slug: string;
  name: string;
  bot_name: string;
  description: string;
  role: Role;
  role_label: string;
  can: string;
  worker_online: boolean;
  running: number;
  queued: number;
  defaults: Record<string, string>;
  icon?: string | null;
  /** the application's code, and the heading the application picker lists it under */
  code?: string | null;
  category?: string | null;
  module?: boolean;
  data_class?: 'STANDARD' | 'FINANCIAL' | 'CONFIDENTIAL_HR';
  /** the environments the application's assistant may use, by name */
  environments?: string[];
  /** where this person's conversations here start, and the highest they may choose (none: any) */
  default_effort?: Effort;
  max_effort?: Effort | null;
}

/** prod | test | dev | copy, read from the environment's name; none when the name does not say */
export type EnvStage = 'prod' | 'test' | 'dev' | 'copy';

export interface ScopeEnvironment {
  id: number;
  environment_name: string | null;
  type: string;
  host_name: string;
  stage?: EnvStage | null;
  /** what Orion reaches it through */
  kind?: 'fusion' | 'database' | 'api' | null;
  /** how up to date the application's study found it ("copied nightly"), as it wrote it */
  fresh?: string | null;
}

export interface ScopeProject {
  project_id: number;
  project_code: string;
  project_name: string;
  is_project_admin: boolean;
  environments: ScopeEnvironment[];
}

export interface MenuButton {
  label: string;
  request: string;
}

export interface MenuPicker {
  id: string;
  placeholder: string;
  template: string;
  options: { label: string; value: string }[];
}

export interface Menu {
  title?: string;
  text: string;
  buttons: MenuButton[];
  pickers: MenuPicker[];
  say?: string[];
  footer?: string;
}

export interface ChatFileRef {
  id: number;
  file_name: string;
  file_size: number;
  mime?: string | null;
  is_image?: boolean;
}

export interface Message {
  id: number;
  thread_id: number;
  kind: MessageKind;
  author: string | null;
  author_name: string | null;
  body: string;
  meta: Record<string, unknown> & {
    files?: ChatFileRef[];
    file?: ChatFileRef;
    options?: MenuButton[];
    used?: boolean;
    chosen?: string;
    by?: string;
    approval_id?: number;
    status?: string;
    answered_by?: string;
    required?: number;
    approvers?: string[];
    patch_id?: number;
    helpful?: number;
    credits?: number;
    file_change?: boolean;
    review_state?: string;
    review_note?: string | null;
    reviewed_by?: string | null;
    requested_by?: string;
    not_helpful?: number;
    name?: string;
    state?: string;
    revision?: number;
    answered_at?: string;
    allow_all?: boolean;
    headline?: string | null;
    note?: string | null;
    preview?: string | null;
    note_after?: string;
    offers_allow_all?: boolean;
    expires_at?: string;
    tool_name?: string;
    steps?: string[];
    waiting?: boolean;
    news?: string;
    elapsed_s?: number;
    percent?: number | null;
    left_s?: number | null;
    based_on?: number | null;
    steps_until?: string | null;
    took_seconds?: number | null;
    error?: string | null;
    command?: boolean;
    buttons?: MenuButton[];
    pickers?: MenuPicker[];
    text?: string;
    title?: string;
    say?: string[];
    footer?: string;
  };
  created_at: string;
  updated_at: string;
}

export interface ThreadSummary {
  id: number;
  channel: string;
  title: string;
  status: 'idle' | 'queued' | 'running';
  project_id: number | null;
  project_code: string | null;
  environment_id: number | null;
  environment_name: string | null;
  started_by: string;
  started_by_name: string;
  last_message_at: string | null;
  pending_approvals: number;
  /** Patches in this conversation handed over or deployed and not yet verified or rolled back. */
  open_patches?: number;
  preview: string;
  preview_kind: string | null;
}

/** How hard Orion thinks on a conversation's requests, as Claude Code names the levels. */
export type Effort = 'low' | 'medium' | 'high' | 'xhigh';
/** Where conversations start and the highest effort: everyone's ("*") and per person; blank inherits. */
/** Private conversations and the auditors who read them with consent (app/chat/privacy.py). */
export type AuditStatus = 'WAITING_SECOND' | 'WAITING_PERSON' | 'ALLOWED' | 'DECLINED' | 'EXPIRED';
export interface AuditAsk {
  id: number; status: AuditStatus; reason: string; reference: string | null; hours: number; requested_at: string;
  auditor: string; firm: string | null; second_by: string | null; access_until: string | null; answered_at: string | null;
}
export interface AuditConversation {
  id: number; application: string; application_code: string; started_by: string; started_by_name: string;
  started_at: string; last_message_at: string | null; requests: number; cost_usd: number;
  my_request: { id: number; status: AuditStatus; access_until: string | null; open_now: boolean } | null;
}
export interface AuditRequest {
  id: number; thread_id: number; application: string | null; person: string; person_name: string;
  requested_by: string; requested_by_name: string; firm: string | null; reason: string; reference: string | null;
  hours: number; requested_at: string; status: AuditStatus; second_by: string | null; second_at: string | null;
  second_note: string | null; person_at: string | null; person_note: string | null; access_until: string | null;
}
export interface Auditor { email: string; name: string | null; firm: string | null; note: string | null; until: string | null; active: boolean; added_by: string; added_at: string }

export interface EffortSetting { email: string; default_effort: Effort | null; max_effort: Effort | null; updated_by?: string | null; updated_at?: string | null }
export interface EffortSettings { default: EffortSetting; people: EffortSetting[]; builtin: Effort }
export const EFFORTS: Effort[] = ['low', 'medium', 'high', 'xhigh'];

/** How long conversations, folders and the query log are kept (Orion > Operations > Retention, app/chat/retention.py). */
export interface RetentionOverview {
  enabled: boolean; conversation_days: number; folder_days: number; query_log_days: number;
  updated_by: string | null; updated_at: string | null;
  last_run_at: string | null; last_result: { conversations: number; messages: number; files: number; query_log_rows: number; more: boolean } | null;
  last_folders_at: string | null; last_folders: { removed: number; bytes: number; kept_busy: number; failed: number } | null;
  minimum: { conversation_days: number; folder_days: number; query_log_days: number };
  preview: { conversations: number; messages: number; files: number; file_bytes: number; query_log_rows: number; held_by_audit: number };
}

/** Alerts on unusual Orion query patterns (Orion > Operations > Alerts, app/chat/alerts.py). */
export type AlertKind = 'SECRET_REFUSED' | 'WRITE_REFUSED' | 'REFUSALS' | 'QUERY_VOLUME' | 'ROWS_READ' | 'TABLE_BREADTH' | 'DAILY_SPIKE' | 'INJECTION_FLAGGED' | 'MODEL_MISMATCH';
export type AlertState = 'OPEN' | 'ACKNOWLEDGED' | 'CLOSED';
export interface OrionAlert {
  id: number; kind: AlertKind; severity: 'warning' | 'high'; status: AlertState;
  person: string | null; application: string | null; first_at: string; last_at: string; count: number;
  detail: { job?: number; n?: number; avg?: number; reason?: string; tables?: string[]; excerpt?: string; pinned?: string; used?: string };
  handled_by: string | null; handled_at: string | null; note: string | null;
}
export interface AlertSettings {
  refusals_per_hour: number; queries_per_hour: number; rows_per_hour: number; tables_per_hour: number;
  spike_factor: number; spike_min: number; recipients: string[];
}
export interface AlertOverview { alerts: OrionAlert[]; open: number; settings: AlertSettings; defaults: AlertSettings }

/** The model register (Orion > Operations > Models, app/chat/pinning.py). */
export type ModelLevel = 'DEFAULT' | 'BEST' | 'BALANCED' | 'FAST';
export type RedteamVerdict = 'PASS' | 'SILENT' | 'FOLLOWED' | 'LEAKED' | 'NOT RUN' | 'NO REPLY' | 'FALSE ALARM';
export interface ModelRun {
  id: number; model_id: string; state: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';
  requested_by: string; requested_at: string | null; started_at: string | null; finished_at: string | null;
  rules_version: string | null; total: number | null; passed: number | null; followed: number | null;
  cost_usd: number | null; error: string | null;
  results?: { id: string; what: string; verdict: RedteamVerdict; why: string[]; reply: string; cost_usd: number | null; seconds: number | null }[];
}
export interface PinnedModel {
  model_id: string; label: string | null; status: 'CANDIDATE' | 'APPROVED' | 'RETIRED';
  retires_on: string | null; note: string | null; added_by: string; added_at: string | null;
  approved_by: string | null; approved_at: string | null; retired_by: string | null; retired_at: string | null;
  single_approved: boolean; approval_reason: string | null;
  levels: ModelLevel[]; latest_run: ModelRun | null; approved_run: ModelRun | null;
  can_approve: boolean; blockers: string[]; current: boolean; retest: string[];
}
export interface ModelRegister {
  levels: { level: ModelLevel; model_id: string | null; set_by: string | null; set_at: string | null }[];
  models: PinnedModel[]; runs: ModelRun[];
  rules_version: string | null; rules_seen_at: string | null;
  mismatch: { job: number; pinned: string; used: string[]; at: string } | null;
  approval_days: number;
}
/** The levels that take more steps and think longer, so each request uses more credits. */
export const COSTLY_EFFORTS: ReadonlySet<Effort> = new Set(['high', 'xhigh']);

export interface Thread {
  id: number;
  channel: string;
  title: string;
  status: 'idle' | 'queued' | 'running';
  project: { id: number; code: string; name: string } | null;
  environment: { id: number; name: string; type: string; stage?: EnvStage | null; kind?: ScopeEnvironment['kind'] } | null;
  /** where else the conversation may be moved to work, and whether the reader may move it */
  environments?: ScopeEnvironment[];
  can_set_environment?: boolean;
  /** a private application's conversation: its starter's alone (app/chat/privacy.py) */
  private?: boolean;
  /** read by an auditor under an allowed request, until then; read-only */
  audit?: { request: number; until: string } | null;
  /** to the person who started it: the auditors' requests to read it, a second auditor agreed */
  audit_requests?: AuditAsk[];
  started_by: string;
  started_by_name: string;
  session_active: boolean;
  allow_all_edits: boolean;
  /** the level the conversation's next requests run at; low unless someone chose otherwise */
  effort: Effort;
  /** the person who started it, or an owner, may change it */
  can_set_effort: boolean;
  /** the highest the viewer may choose here; none: any */
  max_effort?: Effort | null;
  steps_allowed_until?: string | null;
  role: Role;
  role_label: string;
  can_approve: boolean;
  can_approve_edits: boolean;
  edits_rule: 'ASK' | 'ONCE' | 'FREE';
  bot_name: string;
  worker_online: boolean;
  pending_approvals: number;
  created_at: string;
  last_message_at: string | null;
  messages: Message[];
  /** The small print under the conversation: how full its session is, and what it has cost. */
  usage?: ThreadUsage | null;
}

export interface ThreadUsage {
  context_tokens: number;
  context_limit: number;
  /** above this the next request starts a fresh session by itself */
  roll_at: number;
  credits_total: number;
  credits_mine: number;
  last_credits: number | null;
  last_tokens: number | null;
  last_rolled: 'large' | 'stalled' | null;
  /** sessions the reader started in the last hour and 24 hours, against their limits (null: no limit) */
  sessions?: SessionStanding | null;
}

export interface SessionStanding {
  per_hour: number | null;
  per_day: number | null;
  /** the limits are the person's own, not everyone's */
  own: boolean;
  used_hour: number;
  used_day: number;
  may_start: boolean;
  /** why no session may start now: the hourly or daily count, outside working hours, or a limit of 0 */
  waiting_for: 'hour' | 'day' | 'hours' | 'never' | null;
  /** when the next session may start, while it may not now; null with a limit of 0 */
  next_at: string | null;
  /** everyone's working day, HH:MM Riyadh time; "00:00" as an end is midnight */
  day_start: string;
  day_end: string;
  in_hours: boolean;
}

export interface SessionLimitRow {
  email: string;
  per_hour: number | null;
  per_day: number | null;
  used_hour: number;
  used_day: number;
  updated_by: string | null;
  updated_at: string | null;
}

export interface SessionLimits {
  default: { per_hour: number | null; per_day: number | null; day_start: string; day_end: string; updated_by: string | null; updated_at: string | null };
  people: SessionLimitRow[];
}

export interface ThreadState {
  status: string;
  version: string;
  pending_approvals: number;
}

export interface Member {
  email: string;
  role: 'consultant' | 'viewer';
  set_by: string;
  updated_at: string;
}

export interface UploadedFile {
  id: number;
  thread_id: number;
  direction: 'in' | 'out';
  file_name: string;
  file_size: number;
  uploaded_by: string;
  created_at: string;
}

export interface OacConnection {
  environment_id: number;
  name: string;
  connected: boolean;
  until: string | null;
}

export interface OrionApplication {
  id: number;
  code: string;
  name: string;
  description: string | null;
  kind: string;
  data_class: 'STANDARD' | 'FINANCIAL' | 'CONFIDENTIAL_HR';
  status: string;
  default_access: 'PROJECT_MEMBERS' | 'GRANTS_ONLY';
  project_id: number | null;
  project_code: string | null;
  channel_key: string | null;
  slack_channel: string | null;
  in_datapilot: boolean;
  can_serve_in_datapilot: boolean;
  my_role: Role | null;
  my_role_label: string | null;
  my_role_source: 'admin' | 'grant' | 'group' | 'project' | 'hr_clearance' | 'none';
  my_role_expires_at: string | null;
  my_data?: string;
  my_request: string | null;
  can_manage: boolean;
  members: number;
  pending_requests: number;
  approvals_required: number;
  patch_edits: 'ASK' | 'ONCE' | 'FREE';
  model_tier: 'DEFAULT' | 'BEST' | 'BALANCED' | 'FAST';
  /** the exact version each level is pinned to (Operations > Models); a level absent is not pinned */
  model_pins?: Partial<Record<'DEFAULT' | 'BEST' | 'BALANCED' | 'FAST', string>>;
  share_conversations: boolean;
  /** private: a conversation is its starter's alone, Orion only reads, auditors read with consent */
  private_conversations?: boolean;
  category?: string | null;
  /** where a conversation here starts and the highest it may run at; none: everyone's */
  default_effort?: Effort | null;
  max_effort?: Effort | null;
  icon?: string | null;
  hr_cleared?: boolean;
  runtime_state?: 'WAITING' | 'SERVED' | 'REFUSED' | null;
  runtime_note?: string | null;
  runtime?: Record<string, unknown> | null;
  scope_values?: Record<string, string[]>; // on the detail: values a member's data scope can be picked from
  guide_state?: 'NONE' | 'DRAFT' | 'APPROVED';
  open_patches: number;
  review_due: boolean;
  last_reviewed_at: string | null;
  last_reviewed_by: string | null;
  review_every_days: number;
  monthly_budget_usd: number | null;
  spent_month_usd: number | null;
}

export interface OrionGrant {
  id: number;
  principal_type: 'USER' | 'GROUP';
  principal: string;
  role: Role;
  role_label: string;
  hr_cleared: boolean;
  /** a developer: may have Orion deploy patches where the environment allows it */
  can_deploy?: boolean;
  /** a consultant whose patches need no owner's review */
  skip_review?: boolean;
  data_scope: Partial<Record<'BU' | 'LE' | 'PROJECT' | 'LEDGER', string[]>> | null; // every named dimension must hold
  data_label: string;
  expires_at: string | null;
  expired: boolean;
  note: string | null;
  granted_by: string;
  updated_at: string;
}

export interface OrionEnvironment {
  environment_id: number;
  environment_name: string | null;
  type: string | null;
  instance_type: string | null;
  mode: 'READ' | 'CHANGE_WITH_APPROVAL' | 'NONE';
  set_by: string | null;
  project_id?: number | null;
  project_code?: string | null; // set when the environment belongs to another project
  consented_by?: string | null;
}

/** What owners and members read about a study: the plain account, never the guide's technicalities. */
export interface StudyAnalysis {
  name: string;
  studied_at: string | null;
  overview: string | null;
  overview_language: 'en' | 'ar' | null;
  processes: string[];
  systems: string[];
}

/** The Microsoft 365 app registration a study reads private OneDrive / SharePoint links with; never its secret. */
export interface MicrosoftSignIn { tenant_id: string; client_id: string; has_secret: boolean }

export interface StudyStatus {
  running: boolean;
  last: { thread_id: number; status: string; finished_at: string | null; started_at: string } | null;
  study: {
    at?: string; summary: string; overview?: { en?: string; ar?: string };
    schema: { tables: number; views: number; packages: number; apex_app_id: number | null; workspace: string | null; prefixes: { prefix: string; meaning: string }[] };
    transactions: { name: string; tables: string[]; status: string; approval: string }[];
    integrations: { system: string; direction: string; code: string; log: string }[];
    error_sources: { name: string; how: string; recent_rows: string }[];
    dimensions: Record<string, { columns: string[]; tables: string[]; values: string[] }>;
    secrets: { location: string; kind: string }[];
    links: { from: string; to: string; how: string; evidence: string }[];
    freshness: { environment: string; cycle: string; evidence: string }[];
    starters: { label: string; request: string }[];
    from_sources?: { fact: string; url: string; live_check: string }[];
  } | null;
  official_sources?: string[];
  microsoft_sign_in?: MicrosoftSignIn | null;
  guide_proposal: boolean;
}
export interface Readiness { at: string; ok: boolean; checks: { name: string; ok: boolean; detail: string; applies: boolean }[] }
export interface Protection {
  holds_secrets: boolean;
  never_read: string[];
  scope_columns: Record<string, string[]>;
  scope_tables: Record<string, string[]>;
  sync_cards: boolean; // Orion may propose a switched-off ORN_ Data Sync through a card (plan 12.7, 2b)
  defaults: Record<string, string[]>;
  labels: Record<string, string>;
  aliases?: Record<string, number>; // per dimension: how many values carry the id the study recorded behind the name
  from_study: { at: string | null; scope_columns: Record<string, string[]>; scope_tables: Record<string, string[]>; never_read: string[]; secrets: { location: string; kind: string }[] };
  reviewed_by: string | null;
  reviewed_at: string | null;
  managed: boolean;
}
export type ProtectionIn = Pick<Protection, 'holds_secrets' | 'never_read' | 'scope_columns' | 'scope_tables' | 'sync_cards'>;

export interface LinkableProject {
  project_id: number;
  project_code: string;
  project_name: string;
  environments: { id: number; name: string | null; type: string | null; linked: boolean }[];
}

export interface OrionRequest {
  id: number;
  application_code: string;
  email: string;
  name: string | null;
  requested_role: Role;
  reason: string | null;
  status: 'PENDING' | 'APPROVED' | 'DENIED';
  decided_by: string | null;
  decided_at: string | null;
  decision_note: string | null;
  created_at: string;
}

export interface OrionApplicationDetail extends OrionApplication {
  grants: OrionGrant[];
  environments: OrionEnvironment[];
  requests: OrionRequest[];
}

export interface WaitingApproval {
  approval_id: number;
  thread_id: number;
  thread_title: string;
  channel: string;
  application: string;
  requested_by: string;
  tool_name: string;
  lead: string;
  headline: string;
  offers_allow_all: boolean;
  required: number;
  approvers: string[];
  created_at: string;
  expires_at: string | null;
}

/** One script of a deploy or rollback run, as the runner went through it. */
export interface DeployStep {
  file: string;
  title?: string | null;
  success?: string | null;
  of?: string;
  state: 'WAITING' | 'RUNNING' | 'DONE' | 'FAILED' | 'SKIPPED';
  error?: string;
  started_at?: string;
  ended_at?: string;
}
export interface DeployRun {
  id: number;
  action: 'DEPLOY' | 'ROLLBACK';
  state: 'RUNNING' | 'SUCCEEDED' | 'FAILED';
  revision: number;
  steps: DeployStep[];
  error: string | null;
  started_by: string;
  started_by_name: string | null;
  started_at: string | null;
  ended_at: string | null;
  tail: string[];
}
export interface DeployManifest {
  environment: string;
  steps: { file: string; title: string | null; success: string; rollback: string; rollback_success: string | null }[];
  requires: string[];
  before: string | null;
  after: string | null;
}
/** An environment a patch can run on, with the schema (DB user) its scripts run as. */
export interface DeployEnvironment {
  id: number;
  name: string;
  schema: string | null;
  open: boolean;
}
/** What a patch card offers this person: deploy, roll back, or why not. */
export interface DeployReadiness {
  patch_id: number;
  application: string;
  manifest: DeployManifest | null;
  environment: DeployEnvironment | null;
  /** every environment its deploy.json names (a schema can be in development and production) */
  environments: DeployEnvironment[];
  /** where a run of it already changed something: its rollback goes there */
  ran_on: number | null;
  developer: boolean;
  why: 'no_manifest' | 'no_environment' | 'environment_closed' | 'not_developer' | 'busy' | 'review' | null;
  /** the patches its deploy.json requires: id and thread_id when they have a record */
  requires: { name: string; state: string | null; id: number | null; thread_id: number | null }[];
  /** open patches on the same objects, wherever made: first (older, not deployed: it goes first),
   *  newer_live (newer and deployed: this one would undo it), newer (newer, not deployed yet) */
  overlaps: { id: number; label: string; name: string; state: string; thread_id: number | null; requested_by: string; application: string; objects: string[]; kind: 'first' | 'newer_live' | 'newer' }[];
  blocking: string[];
  may_deploy: boolean;
  may_roll_back: boolean;
  run: DeployRun | null;
}

export type PatchState = 'HANDED_OVER' | 'DEPLOYED' | 'VERIFIED' | 'ROLLED_BACK' | 'REPLACED';

export interface OrionPatch {
  id: number;
  application: string;
  application_name: string;
  /** its number in the application, as people say it: MMRMOBILE-028 */
  number: number | null;
  label: string;
  /** what its scripts change ("APEX 295 PAGE 8", "PACKAGE HSE2_SOR_API"); null: not read yet */
  objects: string[] | null;
  name: string;
  state: PatchState;
  revision: number;
  files: ChatFileRef[];
  thread_id: number | null;
  thread_title: string | null;
  channel: string | null;
  requested_by: string;
  handed_over_at: string | null;
  deployed_by: string | null;
  deployed_at: string | null;
  closed_by: string | null;
  closed_at: string | null;
  note: string | null;
  history: { state: string; by: string; at: string; note: string | null }[];
  next: PatchState[];
  review_state: 'NONE' | 'NEEDED' | 'APPROVED' | 'REJECTED';
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  can_review: boolean;
}

export interface OrionHealth {
  application: string;
  name: string;
  channel: string;
  worker_online: boolean;
  worker_seen_at: string | null;
  running: number;
  queued: number;
  oldest_queued_s: number | null;
  requests_24h: number;
  requests_30d: number;
  failed_30d: number;
  median_reply_s: number | null;
  p90_reply_s: number | null;
  median_wait_s: number | null;
  spent_month_usd: number;
  budget_usd: number | null;
  helpful_30d: number;
  not_helpful_30d: number;
  last_error: string | null;
  last_error_at: string | null;
  review_due: boolean;
  last_reviewed_at: string | null;
}

export interface UsageRow {
  requests: number;
  cost_usd: number;
  tokens_in: number;
  tokens_out: number;
  minutes: number;
  credits: number;
}

export interface OrionUsage {
  days: number;
  by_application: (UsageRow & { channel: string; name: string; avg_cost_usd: number; avg_credits: number; fresh_in?: number; cache_read?: number; cache_write?: number; models?: Record<string, number> })[];
  by_person: (UsageRow & { email: string })[];
}

export interface AuditEvent {
  at: string;
  actor: string;
  application: string | null;
  event: string;
  subject: string | null;
  detail: Record<string, unknown>;
}

export interface OrionPerson {
  email: string;
  grants: { application: string; application_name: string; role: Role; hr_cleared: boolean; expires_at: string | null; granted_by: string }[];
  identities: { provider: string; external_id: string; seen_at: string | null }[];
  pending_requests: number;
  requests_90d: number;
  last_request_at: string | null;
}

export interface DirectoryPerson {
  email: string;
  name: string | null;
}

export interface OrionProfileContent {
  intro: string;
  notes: string;
  starters: { label: string; request: string }[];
  timeout_minutes: number | null;
}

export interface OrionProfile {
  version: number;
  status: 'DRAFT' | 'ACTIVE' | 'RETIRED';
  content: OrionProfileContent;
  change_note: string | null;
  created_by: string;
  created_at: string;
  activated_by: string | null;
  activated_at: string | null;
  self_activated: boolean;
}

const ORION = '/orion';

export const orionApi = {
  applications: (includeRetired = false) => api.get<OrionApplication[]>(`${ORION}/applications`, includeRetired ? { include_retired: true } : undefined),
  remove: (code: string) => api.delete<void>(`${ORION}/applications/${code}`),
  application: (code: string) => api.get<OrionApplicationDetail>(`${ORION}/applications/${code}`),
  update: (code: string, body: Partial<Pick<OrionApplication, 'name' | 'description' | 'data_class' | 'default_access' | 'status'>> & { orion_enabled?: boolean; monthly_budget_usd?: number | null; review_every_days?: number; model_tier?: OrionApplication['model_tier']; share_conversations?: boolean; private_conversations?: boolean; category?: string; default_effort?: Effort | ''; max_effort?: Effort | '' }) =>
    api.put<OrionApplication>(`${ORION}/applications/${code}`, body),
  setGrant: (code: string, body: { principal_type: 'USER' | 'GROUP'; principal: string; role: Role; hr_cleared?: boolean; can_deploy?: boolean; skip_review?: boolean; expires_at?: string | null; note?: string; data_scope?: Partial<Record<'BU' | 'LE' | 'PROJECT' | 'LEDGER', string[]>> | null }) =>
    api.put<OrionGrant[]>(`${ORION}/applications/${code}/grants`, body),
  guide: (code: string) => api.get<{ text: string | null; state: 'NONE' | 'DRAFT' | 'APPROVED'; approved_by: string | null; approved_at: string | null; managed: boolean }>(`${ORION}/applications/${code}/guide`),
  saveGuide: (code: string, text: string) => api.put<{ state: string }>(`${ORION}/applications/${code}/guide`, { text }),
  approveGuide: (code: string) => api.post<{ state: string }>(`${ORION}/applications/${code}/guide/approve`, {}),
  analysis: (code: string) => api.get<StudyAnalysis>(`${ORION}/applications/${code}/analysis`),
  study: (code: string) => api.get<StudyStatus>(`${ORION}/applications/${code}/study`),
  startStudy: (code: string) => api.post<{ thread_id: number }>(`${ORION}/applications/${code}/study`, {}),
  oacConnections: (code: string) => api.get<OacConnection[]>(`${ORION}/applications/${code}/oac`),
  connectOac: (environmentId: number, token: string) =>
    api.put(`${ORION}/oac/${environmentId}/connection`, { token }),
  disconnectOac: (environmentId: number) => api.delete(`${ORION}/oac/${environmentId}/connection`),
  newExport: (code: string, exportFileId: number) =>
    api.post<{ file_id: number; name: string; by: string; at: string }>(`${ORION}/applications/${code}/export`, { export_file_id: exportFileId }),
  setOfficialSources: (code: string, sources: string[]) =>
    api.put<{ official_sources: string[] }>(`${ORION}/applications/${code}/official-sources`, { sources }),
  setMicrosoftSignIn: (code: string, body: { tenant_id: string; client_id?: string; client_secret?: string }) =>
    api.put<{ microsoft_sign_in: MicrosoftSignIn | null }>(`${ORION}/applications/${code}/official-sources/microsoft`, body),
  adoptGuide: (code: string) => api.post<{ state: string }>(`${ORION}/applications/${code}/guide/adopt`, {}),
  readiness: (code: string) => api.post<Readiness>(`${ORION}/applications/${code}/readiness`, {}),
  protection: (code: string) => api.get<Protection>(`${ORION}/applications/${code}/protection`),
  saveProtection: (code: string, body: ProtectionIn) => api.put<Protection>(`${ORION}/applications/${code}/protection`, body),
  linkable: (code: string) => api.get<LinkableProject[]>(`${ORION}/applications/${code}/environments/linkable`),
  removeGrant: (code: string, grantId: number) => api.delete<OrionGrant[]>(`${ORION}/applications/${code}/grants/${grantId}`),
  setEnvironment: (code: string, environmentId: number, mode: OrionEnvironment['mode']) =>
    api.put<OrionEnvironment[]>(`${ORION}/applications/${code}/environments`, { environment_id: environmentId, mode }),
  confirmReview: (code: string) => api.post<{ review_due: boolean }>(`${ORION}/applications/${code}/review`, {}),
  health: () => api.get<OrionHealth[]>(`${ORION}/health`),
  usage: (days: number) => api.get<OrionUsage>(`${ORION}/usage`, { days }),
  audit: (application: string | undefined, days: number, kind?: 'access') => api.get<AuditEvent[]>(`${ORION}/audit`, { application, days, kind }),
  auditCsv: (application: string | undefined, days: number, kind?: 'access') =>
    apiClient.get(`${ORION}/audit`, { params: { application, days, format: 'csv', kind }, responseType: 'blob' }).then((r) => r.data as Blob),
  directory: () => api.get<DirectoryPerson[]>(`${ORION}/people/directory`),
  person: (email: string) => api.get<OrionPerson>(`${ORION}/people`, { email }),
  sessionLimits: () => api.get<SessionLimits>(`${ORION}/session-limits`),
  /** email "*" sets everyone's default, with the working day */
  setSessionLimit: (email: string, perHour: number | null, perDay: number | null, day?: { start: string; end: string }) =>
    api.put<SessionLimits>(`${ORION}/session-limits`, { email, per_hour: perHour, per_day: perDay, day_start: day?.start, day_end: day?.end }),
  clearSessionLimit: (email: string) => api.delete<SessionLimits>(`${ORION}/session-limits?email=${encodeURIComponent(email)}`),
  effortSettings: () => api.get<EffortSettings>(`${ORION}/effort-settings`),
  setEffortSetting: (email: string, start: Effort | '', cap: Effort | '') =>
    api.put<EffortSettings>(`${ORION}/effort-settings`, { email, default_effort: start, max_effort: cap }),
  auditMe: () => api.get<{ auditor: boolean; name: string | null; firm: string | null }>(`${ORION}/audit/me`),
  auditConversations: () => api.get<AuditConversation[]>(`${ORION}/audit/conversations`),
  auditRequests: () => api.get<AuditRequest[]>(`${ORION}/audit/requests`),
  auditAsk: (body: { thread_id: number; reason: string; reference?: string; hours: number }) => api.post<AuditRequest>(`${ORION}/audit/requests`, body),
  auditAgree: (id: number, yes: boolean, note?: string) => api.post<AuditRequest>(`${ORION}/audit/requests/${id}/agree`, { yes, note }),
  auditAnswer: (id: number, yes: boolean, note?: string) => api.post<AuditRequest>(`${ORION}/audit/requests/${id}/answer`, { yes, note }),
  auditTrail: () => api.get<AuditEvent[]>(`${ORION}/audit/trail`),
  auditors: () => api.get<Auditor[]>(`${ORION}/audit/auditors`),
  setAuditor: (body: { email: string; name?: string | null; firm?: string | null; note?: string | null; until?: string | null }) => api.put<Auditor[]>(`${ORION}/audit/auditors`, body),
  removeAuditor: (email: string) => api.delete<Auditor[]>(`${ORION}/audit/auditors/${encodeURIComponent(email)}`),
  clearEffortSetting: (email: string) => api.delete<EffortSettings>(`${ORION}/effort-settings?email=${encodeURIComponent(email)}`),
  alerts: (state: AlertState | 'ALL' = 'OPEN') => api.get<AlertOverview>(`${ORION}/alerts`, { state }),
  saveAlertSettings: (body: Partial<AlertSettings>) => api.put<AlertOverview>(`${ORION}/alert-settings`, body),
  handleAlert: (id: number, status: AlertState, note: string | null, state: AlertState | 'ALL') =>
    api.post<AlertOverview>(`${ORION}/alerts/${id}?state=${state}`, { status, note }),
  retention: () => api.get<RetentionOverview>(`${ORION}/retention`),
  saveRetention: (body: Partial<Pick<RetentionOverview, 'enabled' | 'conversation_days' | 'folder_days' | 'query_log_days'>>) => api.put<RetentionOverview>(`${ORION}/retention`, body),
  runRetention: () => api.post<RetentionOverview>(`${ORION}/retention/run`),
  models: () => api.get<ModelRegister>(`${ORION}/models`),
  addModel: (body: { model_id: string; label?: string | null; retires_on?: string | null; note?: string | null }) => api.post<ModelRegister>(`${ORION}/models`, body),
  editModel: (id: string, body: { label?: string | null; retires_on?: string | null; note?: string | null }) => api.put<ModelRegister>(`${ORION}/models/${encodeURIComponent(id)}`, body),
  redteamModel: (id: string) => api.post<ModelRegister>(`${ORION}/models/${encodeURIComponent(id)}/redteam`),
  /** reason: required when the approver added the model (no second administrator available) */
  approveModel: (id: string, reason?: string) => api.post<ModelRegister>(`${ORION}/models/${encodeURIComponent(id)}/approve`, { reason: reason ?? null }),
  retireModel: (id: string) => api.post<ModelRegister>(`${ORION}/models/${encodeURIComponent(id)}/retire`),
  setModelLevel: (level: ModelLevel, modelId: string | null) => api.put<ModelRegister>(`${ORION}/model-levels/${level}`, { model_id: modelId }),
  modelRun: (id: number) => api.get<ModelRun>(`${ORION}/model-runs/${id}`),
  offboard: (email: string) => api.post<{ grants: number; identities: number; requests: number }>(`${ORION}/people/offboard`, { email }),
  patches: (application?: string, state?: string, review?: string) => api.get<OrionPatch[]>(`${ORION}/patches`, { application, state, review }),
  reviewPatch: (id: number, approve: boolean, note?: string) => api.post<OrionPatch>(`${ORION}/patches/${id}/review`, { approve, note: note || null }),
  setEditsRule: (code: string, rule: 'ASK' | 'ONCE' | 'FREE') => api.put<OrionApplication>(`${ORION}/applications/${code}/edits-rule`, { rule }),
  movePatch: (id: number, to: PatchState, note?: string) => api.post<OrionPatch>(`${ORION}/patches/${id}/move`, { to, note: note || null }),
  deployReadiness: (id: number, environmentId?: number) =>
    api.get<DeployReadiness>(`${ORION}/patches/${id}/deploy`, environmentId ? { environment_id: environmentId } : undefined),
  deploy: (id: number, action: 'DEPLOY' | 'ROLLBACK', environmentId?: number) =>
    api.post<DeployReadiness>(`${ORION}/patches/${id}/deploy`, { action, confirmed: true, environment_id: environmentId }),
  setApprovalRule: (code: string, required: number) => api.put<OrionApplication>(`${ORION}/applications/${code}/approval-rule`, { required }),
  profiles: (code: string) => api.get<OrionProfile[]>(`${ORION}/applications/${code}/profiles`),
  saveProfile: (code: string, content: OrionProfileContent, changeNote: string) =>
    api.post<OrionProfile>(`${ORION}/applications/${code}/profiles`, { content, change_note: changeNote || null }),
  activateProfile: (code: string, version: number, force: boolean) =>
    api.post<OrionProfile>(`${ORION}/applications/${code}/profiles/${version}/activate`, { force }),
  requestAccess: (code: string, role: Role, reason: string) => api.post<OrionRequest>(`${ORION}/applications/${code}/requests`, { role, reason }),
  decide: (requestId: number, body: { approve: boolean; role?: Role; expires_at?: string | null; note?: string }) =>
    api.post<OrionRequest>(`${ORION}/requests/${requestId}/decide`, body),
  redeem: (code: string) => api.post<{ provider: string; external_id: string }>(`${ORION}/me/identities/redeem`, { code }),
  identities: () => api.get<{ provider: string; external_id: string; email: string; linked_at: string }[]>(`${ORION}/me/identities`),
};

export interface MessageTranslation {
  message_id: number;
  /** translated: body holds it; same: already in the reader's language; empty: nothing to
   *  translate; failed: could not be translated without risking a value, so not shown */
  status: 'translated' | 'same' | 'empty' | 'failed';
  source_language: 'en' | 'ar' | null;
  body: string | null;
}

export const chatApi = {
  channels: () => api.get<Channel[]>(`${BASE}/channels`),
  access: (channel: string) => api.get<ScopeProject[]>(`${BASE}/channels/${channel}/access`),
  menu: (channel: string, projectId?: number | null, environmentId?: number | null) =>
    api.get<Menu>(`${BASE}/channels/${channel}/menu`, {
      project_id: projectId ?? undefined,
      environment_id: environmentId ?? undefined,
    }),
  members: (channel: string) => api.get<Member[]>(`${BASE}/channels/${channel}/members`),
  setMember: (channel: string, email: string, role: 'consultant' | 'viewer') =>
    api.put<Member[]>(`${BASE}/channels/${channel}/members`, { email, role }),
  removeMember: (channel: string, email: string) =>
    api.delete<Member[]>(`${BASE}/channels/${channel}/members/${encodeURIComponent(email)}`),

  approvals: () => api.get<WaitingApproval[]>(`${BASE}/approvals`),
  rate: (threadId: number, messageId: number, rating: 1 | -1, reason?: string) =>
    api.post<Message[]>(`${BASE}/threads/${threadId}/messages/${messageId}/feedback`, { rating, reason: reason || null }),
  threads: (channel?: string, projectId?: number | null, startedBy?: string, q?: string) =>
    api.get<ThreadSummary[]>(`${BASE}/threads`, { channel, project_id: projectId ?? undefined, started_by: startedBy || undefined, q: q || undefined, limit: 100 }),
  createThread: (body: { channel: string; project_id?: number | null; environment_id?: number | null; text?: string; file_ids?: number[]; effort?: Effort }) =>
    api.post<Thread>(`${BASE}/threads`, body),
  thread: (id: number) => api.get<Thread>(`${BASE}/threads/${id}`),
  rename: (id: number, title: string) => apiClient.patch<Thread>(`${BASE}/threads/${id}`, { title }).then((r) => r.data),
  setEffort: (id: number, effort: Effort) => api.put<Thread>(`${BASE}/threads/${id}/effort`, { effort }),
  setEnvironment: (id: number, environmentId: number | null) => api.put<Thread>(`${BASE}/threads/${id}/environment`, { environment_id: environmentId }),
  stop: (id: number) => api.post<Message[]>(`${BASE}/threads/${id}/stop`, {}),
  state: (id: number) => api.get<ThreadState>(`${BASE}/threads/${id}/state`),
  send: (id: number, text: string, fileIds: number[] = []) =>
    api.post<Message[]>(`${BASE}/threads/${id}/messages`, { text, file_ids: fileIds }),
  approve: (id: number, approvalId: number, choice: 'allow' | 'deny' | 'allow_all') =>
    api.post<Message[]>(`${BASE}/threads/${id}/approvals/${approvalId}`, { choice }),
  press: (id: number, body: { message_id: number; value?: string; picker_id?: string; option?: string }) =>
    api.post<Message[]>(`${BASE}/threads/${id}/press`, body),
  /** Messages in the reader's language, for reading only; the messages themselves are unchanged. */
  translate: (id: number, messageIds: number[], to: 'en' | 'ar') =>
    apiClient
      .post<MessageTranslation[]>(`${BASE}/threads/${id}/translate`, { message_ids: messageIds, to }, { timeout: 180_000 })
      .then((r) => r.data),
  upload: (id: number, file: File) => {
    const form = new FormData();
    form.append('file', file, file.name);
    return apiClient
      .post<UploadedFile>(`${BASE}/threads/${id}/files`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300_000,
      })
      .then((r) => r.data);
  },
  /** The URL the browser can open or embed; the token travels with the download below. */
  async downloadBlob(fileId: number): Promise<{ blob: Blob; name: string }> {
    const r = await apiClient.get(`${BASE}/files/${fileId}/download`, { responseType: 'blob', timeout: 300_000 });
    const disposition = String(r.headers['content-disposition'] ?? '');
    const m = /filename="?([^";]+)"?/.exec(disposition);
    return { blob: r.data as Blob, name: m ? m[1] : `file-${fileId}` };
  },
};

export default chatApi;
